import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Target, Play, ArrowRight } from "lucide-react";
import ExerciseIcon from "@/components/ExerciseIcon";

/**
 * WeaknessCard — client-side dashboard card that surfaces the coach's
 * "Weakness of the week" pick along with the top 3 curated exercises for
 * that muscle group.  Silently renders nothing when there's no coach or no
 * active focus.
 */
export default function WeaknessCard() {
  const [focus, setFocus] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/coaching/weakness/mine");
        if (!alive) return;
        setFocus(r.data);
        if (r.data?.muscle_group) {
          const ex = await api.get("/exercises", {
            params: { muscle: r.data.muscle_group },
          });
          if (!alive) return;
          setExercises((ex.data || []).slice(0, 3));
        }
      } catch {
        /* silent — no coach or unauthenticated */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading || !focus) return null;

  return (
    <div
      className="obs-card mb-6 border-blaze/40"
      data-testid="weakness-card"
    >
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Target className="h-5 w-5 text-blaze" />
          <div>
            <div className="overline text-blaze">
              Focus this week · picked by {focus.coach_name}
            </div>
            <h3 className="font-heading text-3xl uppercase mt-1">
              {focus.muscle_group}
            </h3>
          </div>
        </div>
        <Link
          to={`/exercises?muscle=${encodeURIComponent(focus.muscle_group)}`}
          className="ghost-btn px-3 py-1.5 text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
          data-testid="weakness-see-all"
        >
          See all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {focus.note && (
        <p className="text-sm text-zinc-300 mb-4 whitespace-pre-wrap">
          {focus.note}
        </p>
      )}

      {exercises.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {exercises.map((ex) => (
            <Link
              key={ex.id}
              to={`/exercises?id=${ex.id}`}
              data-testid={`weakness-ex-${ex.id}`}
              className="border border-obsidian-600 rounded-sm overflow-hidden bg-obsidian-900/40 hover:border-blaze transition-colors flex flex-col"
            >
              <ExerciseIcon exercise={ex} size="hero" />
              <div className="p-3">
                <div className="font-heading text-sm uppercase truncate">{ex.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 truncate">
                  {ex.muscles.slice(0, 3).join(" · ")}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-4 flex gap-2 flex-wrap">
        <Link
          to="/workouts"
          className="blaze-btn px-4 py-2 text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
          data-testid="weakness-log-cta"
        >
          <Play className="h-3 w-3" /> Log a {focus.muscle_group} session
        </Link>
      </div>
    </div>
  );
}
